<script setup lang="ts">
/**
 * HomeSearchWizardProgress — barre d'étapes du parcours de l'accueil
 * (DESIGN.md « Barre d'étapes », EXPERIENCE.md « Barre d'étapes »).
 *
 * `<ol>` de 5 marqueurs carrés (coins 8 px, pas de rond : le rond plein est le
 * signe de la chambre d'hôtes dans la charte). ≥ md, étapes empilées en
 * colonne — marqueur de 36 px à gauche, nom et état à droite. Le picto de l'étape porte le sens, le remplissage porte l'état —
 * aucun trait, pour ne pas se confondre avec le soulignement de l'accroche :
 * - étape faite = coche, vrai `<button>` (nom + récapitulatif souligné) qui y revient ;
 * - étape courante = picto sur aplat sage-700, `aria-current="step"`, « En cours » ;
 * - étapes à venir = picto atténué dans un contour, texte inerte (« À venir », « Facultatif » pour la 5).
 * On ne saute jamais en avant.
 *
 * < md : libellés en `sr-only`, marqueurs seuls (celui d'une étape faite
 * reste un bouton de 44 px de haut), plus la ligne « Étape n sur 5 · Nom ».
 */

export interface WizardProgressStep {
  /** Nom de l'étape (« Type de séjour »). */
  name: string
  /** Picto Lucide de l'étape (`i-lucide-map-pin`). */
  icon: string
  /** Récapitulatif de la réponse, affiché une fois l'étape faite. */
  value?: string
  /** Étape facultative (« Facultatif » au lieu de « À venir »). */
  optional?: boolean
}

const props = defineProps<{
  steps: WizardProgressStep[]
  /** Étape courante, à partir de 1. */
  current: number
}>()

const emit = defineEmits<{
  go: [step: number]
}>()

const { t } = useI18n()

const items = computed(() =>
  props.steps.map((step, i) => {
    const n = i + 1
    return {
      n,
      name: step.name,
      icon: step.icon,
      value: step.value ?? '',
      status: n < props.current ? 'done' : n === props.current ? 'current' : 'future',
      futureText: step.optional
        ? t('pages.home.wizard.progress.optional')
        : t('pages.home.wizard.progress.upcoming'),
    } as const
  }),
)

const currentName = computed(() => props.steps[props.current - 1]?.name ?? '')
</script>

<template>
  <div class="flex flex-col gap-2.5">
    <!-- ≥ md : marqueur à gauche, nom + état à droite ; < md, marqueurs seuls
         en ligne (le bloc de texte est `sr-only`). -->
    <ol
      :aria-label="$t('pages.home.wizard.progress.ariaLabel')"
      class="m-0 grid list-none grid-cols-5 gap-1.5 p-0 md:grid-cols-1 md:gap-4"
    >
      <li
        v-for="item in items"
        :key="item.n"
        class="flex min-w-0 flex-col"
        :aria-current="item.status === 'current' ? 'step' : undefined"
      >
        <!-- Faite : bouton qui y revient -->
        <button
          v-if="item.status === 'done'"
          type="button"
          class="group flex min-h-11 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-sm text-center text-paper-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sage-700 md:flex-row md:items-start md:justify-start md:gap-3 md:text-left"
          @click="emit('go', item.n)"
        >
          <span
            aria-hidden="true"
            class="flex size-9 flex-none items-center justify-center rounded-lg border-[1.5px] border-sage-700 bg-sage-100 text-sage-700 transition-colors group-hover:bg-sage-700 group-hover:text-paper-50"
          >
            <UIcon name="i-lucide-check" class="size-5" />
          </span>
          <span class="sr-only md:not-sr-only md:flex md:min-w-0 md:flex-col md:gap-0.5">
            <span class="block text-[17px] font-medium leading-[1.3]"><span class="sr-only">{{ item.n }}. </span>{{ item.name }}</span>
            <span class="block text-base leading-[1.3] text-paper-900/75 underline underline-offset-4 break-words group-hover:text-paper-900">{{ item.value }}</span>
          </span>
        </button>

        <!-- Courante -->
        <div
          v-else-if="item.status === 'current'"
          class="flex min-h-11 flex-col items-center justify-center gap-2 text-center md:flex-row md:items-start md:justify-start md:gap-3 md:text-left"
        >
          <span
            aria-hidden="true"
            class="flex size-9 flex-none items-center justify-center rounded-lg bg-sage-700 text-paper-50"
          >
            <UIcon :name="item.icon" class="size-5" />
          </span>
          <span class="sr-only md:not-sr-only md:flex md:min-w-0 md:flex-col md:gap-0.5">
            <span class="block text-[17px] font-medium leading-[1.3]"><span class="sr-only">{{ item.n }}. </span>{{ item.name }}</span>
            <span class="block text-base font-medium leading-[1.3] text-sage-700">{{ $t('pages.home.wizard.progress.current') }}</span>
          </span>
        </div>

        <!-- À venir : inerte -->
        <div
          v-else
          class="flex min-h-11 flex-col items-center justify-center gap-2 text-center md:flex-row md:items-start md:justify-start md:gap-3 md:text-left"
        >
          <span
            aria-hidden="true"
            class="flex size-9 flex-none items-center justify-center rounded-lg border-[1.5px] border-sage-500 text-paper-900/75"
          >
            <UIcon :name="item.icon" class="size-5" />
          </span>
          <span class="sr-only md:not-sr-only md:flex md:min-w-0 md:flex-col md:gap-0.5">
            <span class="block text-[17px] font-medium leading-[1.3] text-paper-900/75"><span class="sr-only">{{ item.n }}. </span>{{ item.name }}</span>
            <span class="block text-base leading-[1.3] text-paper-900/75">{{ item.futureText }}</span>
          </span>
        </div>
      </li>
    </ol>

    <!-- < md : « Étape n sur 5 · Nom » lisible par tous ; le slot y pose le retour. -->
    <div class="flex items-center justify-between gap-3 md:hidden">
      <p class="m-0 min-w-0 text-[17px] font-medium leading-[1.3] text-paper-900">
        {{ $t('pages.home.wizard.progress.stepOf', { n: current, total: steps.length, name: currentName }) }}
      </p>
      <slot name="mobile-action" />
    </div>
  </div>
</template>
